import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import Button, { ButtonSize } from 'packages/kakapo-ui-kit/components/editors/Button';

import JobCompetencyEditorModal from './JobCompetencyEditor';
import JobCompetencyRow from './JobCompetencyRow';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';
import EmptyStateCard from 'packages/kakapo-ui-kit/components/cards/EmptyStateCard';
import DropdownList, { DropdownListItem as BaseDropdownListItem } from 'packages/kakapo-ui-kit/components/lists/DropdownList';
import { HorizontalAlignment } from 'packages/kakapo-ui-kit/types/Layout';
import { AddIcon, MagicWandIcon } from '@treams/treams-icons';
import JobCompetencyGeneratorModal from './JobCompetencyGenerator';

const TabBar = styled(Flex)`
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlue};
	align-items: center;
`;

const Tab = styled(Box)<{ selected?: boolean }>`
	margin-bottom: -1px;
	font-size: 14px;
	padding: 18px 0px;
	margin-right: 44px;
	cursor: pointer;

	color: ${(props) => (props.selected ? props.theme.colors.primary : props.theme.colors.slateBlue)};
	border-bottom: 1px solid
		${(props) => (props.selected ? props.theme.colors.primary : props.theme.colors.lightBlue)};

	${(props) =>
		!props.selected &&
		`
	&:hover {
		color: black;
		border-bottom: 1px solid ${props.theme.colors.slateBlue};
	}`}
`;

const DropdownListItem = styled(BaseDropdownListItem)`
	padding: 5px 22px;

	a {
		display: flex;
		align-items: center;
		gap: 10px;
		font-size: 16px;
	}
`;

const Icon = styled(Box)`
	display: inline-block;
	width: 20px;
	height: 20px;
`;

export default function JobCompetenciesEditor(props: { jobTitle: graphql.JobTitle }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const hasSkillsAI = useFeature('2024.skills.ai');

	const [adding, setAdding] = useState(false);
	const [generating, setGenerating] = useState(false);

	const levels = _.sortBy(
		_.uniqBy(
			props.jobTitle.competencies.map(({ level }) => level),
			(level) => level?.id,
		),
		(competency) => localize(competency?.title ?? [])?.text ?? '',
	);

	const [selectedLevel, setSelectedLevel] = useState(levels[0]?.id);
	const [isAddDropdownHidden, setAddDropdownHidden] = useState(true);

	const addOptions = [
		{
			label: t('settings.jobCompetencies.addExisting'),
			icon: (<Icon><AddIcon /></Icon>),
			action: () => setAdding(true),
		},
		{
			label: t('settings.jobCompetencies.addGenerated'),
			icon: (<Icon><MagicWandIcon /></Icon>),
			action: () => setGenerating(true),
		}
	];

	const toggleAddDropdown = () => {
		if (hasSkillsAI) {
			setAddDropdownHidden((current) => !current);
		} else {
			setAdding(true);
		}
	};

	const addDropDown = !isAddDropdownHidden && (
		<DropdownList
			horizontalAlignment={props.jobTitle.competencies.length > 0
				? HorizontalAlignment.Right 
				: HorizontalAlignment.Left
			}
			width="300px"
		>
			{addOptions.map(({ label, icon, action }, idx) => (
				<DropdownListItem key={`LanguageSelectorDropdownItem-${idx}`}>
					{/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
					<a
						href="#"
						onClick={(event) => {
							event.preventDefault();
							action();
							setAddDropdownHidden(true);
						}}
					>
						{icon} <span>{label}</span>
					</a>
				</DropdownListItem>
			))}
		</DropdownList>
	);

	const competencies = props.jobTitle.competencies.filter(
		(competency) => competency.level?.id === selectedLevel,
	);

	useEffect(() => {
		if (competencies.length === 0 && levels.length > 0) {
			setSelectedLevel(levels[0]?.id);
		}
	}, [competencies.length, levels.length]);

	return (
		<>
			{!props.jobTitle.competencies.length ? (
				<Box mt={3}>
					<EmptyStateCard
						title={t('settings.jobCompetencies.empty.title')}
						button={{
							title: t('settings.jobCompetencies.addButton.title'),
							onClick: toggleAddDropdown,
						}}
						buttonComplement={addDropDown}
					/>
				</Box>
			) : (
				<>
					<TabBar>
						{levels.map((level) => (
							<Tab
								key={level?.id ?? '*'}
								selected={selectedLevel === level?.id}
								onClick={() => setSelectedLevel(level?.id)}
							>
								{level ? localize(level!.title).text : t('settings.jobCompetencies.allLevels')}
							</Tab>
						))}
						<Box flexGrow={1} />
						<Box flexGrow={0} flexShrink={0} style={{ position: 'relative' }}>
							<Button size={ButtonSize.Small} onClick={toggleAddDropdown}>
								{t('settings.jobCompetencies.addButton.title')}
							</Button>
							{addDropDown}
						</Box>
					</TabBar>
					{competencies.map((competency) => (
						<JobCompetencyRow
							key={competency.id}
							jobCompetency={competency as graphql.JobCompetency}
							jobTitle={props.jobTitle}
							jobLevelID={selectedLevel}
						/>
					))}
				</>
			)}
			{adding && (
				<JobCompetencyEditorModal jobTitle={props.jobTitle} onDismiss={() => setAdding(false)} />
			)}
			{generating && (
				<JobCompetencyGeneratorModal jobTitle={props.jobTitle} onDismiss={() => setGenerating(false)} />
			)}
		</>
	);
}
